import { useState, useCallback, useEffect } from 'react'
import useTranslation from 'next-translate/useTranslation'
import {
  BoxPickUpStation,
  OrderFormat,
  ServiceTime,
  ServiceTimeKindType,
} from '@ancon/wildcat-types'
import moment from 'moment'
import noop from 'lodash/noop'

import Modal from '../../../app/components/modal/Modal'
import { ModalHeaderProps } from '../../../app/components/modal/ModalHeader'
import Button from '../../../app/components/Button'
import BodyText from '../../../app/components/BodyText'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  outletIsVisibleBoxPickupOrderConfigureModalSelector,
  outletListItemSelector,
} from '../../store/outletSelector'
import { AppRoutes } from '../../../app/constants'
import useRouteMatch from '../../../app/hooks/useRouteMatch'
import { outletSetIsVisibleBoxPickupOrderConfigureModal } from '../../store/outletSlice'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import OrderConfigurationModalHeader from '../orderConfigurationModal/OrderConfigurationModalHeader'
import OrderTimeSelectionStep from '../orderConfigurationModal/OrderTimeSelectionStep'
import OrderConfigureTimeInfo from '../../../outlets/components/newOrderTimeConfigureModal/OrderConfigureTimeInfo'
import { fetchOutletStations } from '../../store/outletThunks'
import { checkoutCurrentCheckoutOutletListItemSelector } from '../../../checkout/store/checkoutSelectors'

import styles from './BoxPickupOrderConfigureModal.module.scss'
import BoxPickupSections from './BoxPickupSections'

const initialServiceTime = {
  kind: ServiceTimeKindType.AtSpecifiedTime,
  time: '',
}

type BoxPickupOrderConfigureModalProps = {
  onClickContinue: ({
    serviceTime,
    section,
  }: {
    serviceTime: ServiceTime
    section: BoxPickUpStation
  }) => void
}

export default function BoxPickupOrderConfigureModal({
  onClickContinue,
}: BoxPickupOrderConfigureModalProps) {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()
  const [isShowTimePicker, setIsShowTimePicker] = useState(true)
  const [serviceTime, setServiceTime] =
    useState<ServiceTime>(initialServiceTime)
  const [selectedSection, setSelectedSection] = useState<BoxPickUpStation>()
  const checkoutOutletListItem = useAppSelector(
    checkoutCurrentCheckoutOutletListItemSelector,
  )
  const [sections, setSections] = useState<BoxPickUpStation[]>([])
  const [isSectionsFetching, setIsSectionsFetching] = useState(false)
  const isOutletPage = useRouteMatch([`${AppRoutes.Outlet}/`])
  const isOpen = useAppSelector(
    outletIsVisibleBoxPickupOrderConfigureModalSelector,
  )
  const outletOutletListItem = useAppSelector(outletListItemSelector)
  const outletListItem = isOutletPage
    ? outletOutletListItem
    : checkoutOutletListItem
  const pickerTimeMoment = serviceTime?.time
    ? moment(serviceTime.time)
    : undefined

  useEffect(() => {
    // Reset state when modal is closed
    if (!isOpen) {
      setServiceTime(initialServiceTime)
      setSelectedSection(undefined)
      setSections([])
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setIsSectionsFetching(true)
      setIsShowTimePicker(true)
      dispatch(fetchOutletStations({ outletId: outletListItem?.id! }))
        .unwrap()
        .then(({ items }) => {
          setSections(items)
        })
        .catch(noop)
        .finally(() => {
          setIsSectionsFetching(false)
        })
    }
  }, [dispatch, isOpen, outletListItem?.id])

  function handleOnPressBack() {
    setIsShowTimePicker(false)
  }

  function handleOnClickScheduleOrder() {
    setIsShowTimePicker(true)
  }

  function handleOnChangeServiceTime(newServiceTime: ServiceTime) {
    setServiceTime(newServiceTime)
    setIsShowTimePicker(false)
  }

  function handleOnClose() {
    dispatch(outletSetIsVisibleBoxPickupOrderConfigureModal(false))
    setServiceTime(initialServiceTime)
    setIsShowTimePicker(false)
  }

  const isCloseEnabled = !isShowTimePicker || !pickerTimeMoment

  const CustomHeader = useCallback(
    (headerProps: ModalHeaderProps) => (
      <OrderConfigurationModalHeader
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...headerProps}
        onBack={handleOnPressBack}
        isSecondStep={!isCloseEnabled}
      />
    ),
    [isCloseEnabled],
  )

  function handleOnClickContinue() {
    onClickContinue({ serviceTime, section: selectedSection! })
    dispatch(outletSetIsVisibleBoxPickupOrderConfigureModal(false))
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t(
        isShowTimePicker
          ? 'newOrderTimeConfigureModal.scheduleOrder'
          : 'newOrderTimeConfigureModal.title',
      )}
      onClose={handleOnClose}
      className={styles.modal}
      mobileModalMode={isShowTimePicker ? 'full-screen' : 'bottom-sheet'}
      CustomModalHeader={CustomHeader}
    >
      {isShowTimePicker ? (
        <OrderTimeSelectionStep
          selectedOrderFormat={OrderFormat.BoxPickup}
          initialServiceTime={serviceTime}
          outletListItem={outletListItem!}
          onApply={handleOnChangeServiceTime}
        />
      ) : (
        <div className={styles.wrapper}>
          <BodyText className={styles.infoText}>
            {t('newOrderTimeConfigureModal.boxPickup.whenInfo')}
          </BodyText>
          <section className={styles.section}>
            <OrderConfigureTimeInfo
              serviceTime={serviceTime}
              onClickScheduleOrder={handleOnClickScheduleOrder}
            />
            <BoxPickupSections
              onChangeSection={setSelectedSection}
              selectedSection={selectedSection}
              sections={sections}
              isLoading={isSectionsFetching}
            />
          </section>
          <Button
            className={styles.continueButton}
            disabled={!pickerTimeMoment || !selectedSection}
            onClick={handleOnClickContinue}
          >
            {t('newOrderTimeConfigureModal.continue')}
          </Button>
        </div>
      )}
    </Modal>
  )
}
